
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { RecommendedDealersHeader } from "./recommended-dealers/RecommendedDealersHeader";
import { RecommendedDealersFooter } from "./recommended-dealers/RecommendedDealersFooter";
import { RecommendedDealersList } from "./recommended-dealers/RecommendedDealersList";
import { RecommendedDealersLoading } from "./recommended-dealers/RecommendedDealersLoading";
import { RecommendedDealersPlaceholder } from "./recommended-dealers/RecommendedDealersPlaceholder";
import { RecommendedDealersError } from "./recommended-dealers/RecommendedDealersError";
import { EmptyDealersState } from "./EmptyDealersState";
import { useRecommendedDealers } from "@/hooks/recommended-dealers/useRecommendedDealersCore";
import { useNavigate } from "react-router-dom";
import { usePriceStore } from "@/hooks/usePriceStore";
import { useDealerUtils } from "@/hooks/dealers/useDealerUtils";
import { useStore } from "@/store/useStore";
import { useSupabaseAuth } from "@/contexts/auth/SupabaseAuthContext";

interface RecommendedDealersProps {
  initialMetal?: "gold" | "silver";
}

export function RecommendedDealers({ initialMetal = "gold" }: RecommendedDealersProps) {
  const navigate = useNavigate();
  const { createDealerCanonicalPath } = useDealerUtils();
  // Get the price store to access currently selected metal and purity
  const { goldPurity, silverPurity, rawGoldPrice, rawSilverPrice, calculateGoldPrice, calculateSilverPrice } = usePriceStore();
  // Get the authentication state and dealer functions
  const { fetchFavoriteDealers } = useStore();
  const { isAuthenticated, user } = useSupabaseAuth();
  
  // State to track currently selected metal for the dealers list
  const [selectedMetal, setSelectedMetal] = useState<"gold" | "silver">(initialMetal);
  const [forceRender, setForceRender] = useState(0);
  
  // Refs to prevent excessive fetching
  const lastAuthState = useRef<string | null>(null);
  const favoritesFetched = useRef(false);
  const fetchTimeout = useRef<NodeJS.Timeout | null>(null);
  
  // Handle initial metal setup
  useEffect(() => {
    if (initialMetal) {
      setSelectedMetal(initialMetal);
    }
  }, [initialMetal]);
  
  // Get recommended dealers with the selected metal
  const metalType = useMemo(() => 
    selectedMetal === "gold" ? "ORO" : "ARGENTO", 
    [selectedMetal]
  );
  
  const { 
    dealers, 
    isLoading, 
    isError, 
    refresh,
    shouldShowError
  } = useRecommendedDealers(metalType);
  
  // Immediate rendering when dealers are available
  useEffect(() => {
    if (dealers.length > 0) {
      console.log("🔄 RecommendedDealers: Dealers available, enabling immediate render", {
        dealersCount: dealers.length,
        selectedMetal
      });
      setForceRender(prev => prev + 1);
    }
  }, [dealers.length, selectedMetal]);
  
  // Enhanced logging for debugging
  useEffect(() => {
    console.log("🏠 RecommendedDealers: Component state", {
      dealersCount: dealers.length,
      isLoading,
      isError,
      selectedMetal,
      metalType,
      forceRender
    });
  }, [dealers.length, isLoading, isError, selectedMetal, metalType, forceRender]);
  
  // Memoized auth state to prevent unnecessary re-renders
  const authStateKey = `${isAuthenticated}-${user?.id || 'none'}`;
  
  // Fetch user's favorite dealers when authentication state changes (with anti-loop protection)
  useEffect(() => {
    // Check if auth state actually changed
    if (lastAuthState.current === authStateKey) {
      return;
    }
    
    // Clear any pending fetch timeout
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }
    
    if (isAuthenticated && user?.id && !favoritesFetched.current) {
      console.log("🔐 RecommendedDealers: Auth state changed, fetching favorites", {
        userId: user.id,
        wasAuthenticated: lastAuthState.current?.includes('true'),
        nowAuthenticated: isAuthenticated
      });
      
      // Debounce the fetch to prevent rapid calls
      fetchTimeout.current = setTimeout(() => {
        fetchFavoriteDealers();
        favoritesFetched.current = true;
        lastAuthState.current = authStateKey;
      }, 500);
    } else if (!isAuthenticated) {
      // Reset when user logs out
      favoritesFetched.current = false;
      lastAuthState.current = authStateKey;
    }
    
    return () => {
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, [authStateKey, isAuthenticated, user?.id, fetchFavoriteDealers]);
  
  // Calculate the market price based on the selected metal and purity
  const marketPrice = selectedMetal === "gold" 
    ? (rawGoldPrice ? calculateGoldPrice(rawGoldPrice, goldPurity) : 0)
    : (rawSilverPrice ? calculateSilverPrice(rawSilverPrice, silverPurity, 'g') : 0);
  
  // Handle dealer selection
  const handleDealerClick = useCallback((dealerId: string) => {
    // Find the dealer to get its city
    const dealer = dealers.find(d => d.id === dealerId);
    
    if (dealer && dealer.city) {
      // Create a canonical path with the city
      const path = createDealerCanonicalPath(dealerId, dealer.city);
      navigate(path);
    } else {
      // Fallback to "roma" if no city is available
      navigate(`/compro-oro/roma/${dealerId}`);
    }
  }, [navigate, dealers, createDealerCanonicalPath]);
  
  // Memoized refresh function to prevent recreation
  const memoizedRefresh = useCallback(() => {
    console.log("🔄 RecommendedDealers: Manual refresh triggered");
    refresh();
  }, [refresh]);
  
  // Get the current purity to display based on metal type
  const currentPurity = selectedMetal === "gold" ? goldPurity : silverPurity;
  
  return (
    <Card className="border border-metal-light/20 border-2 metal-border bg-gradient-to-b from-metal/5 to-transparent">
      <RecommendedDealersHeader 
        onRefresh={memoizedRefresh} 
        goldPurity={currentPurity}
        metal={selectedMetal} 
      />
      
      <CardContent className="p-0" key={`dealers-${forceRender}`}>
        {dealers.length > 0 ? (
          <RecommendedDealersList 
            key={`dealers-list-${dealers.length}-${selectedMetal}-${forceRender}`}
            dealers={dealers} 
            marketPrice={marketPrice} 
            goldPurity={currentPurity}
            metal={selectedMetal}
            onDealerClick={handleDealerClick} 
          />
        ) : (
          <RecommendedDealersLoading />
        )}
      </CardContent>
      
      <RecommendedDealersFooter 
        selectedMetal={selectedMetal} 
        onMetalChange={setSelectedMetal} 
      />
    </Card>
  );
}
